// src/components/ui/Avatar.tsx
import * as React from 'react';
import { cn } from '@/lib/utils';
import { User } from 'lucide-react';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  fallback?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

function getInitials(name?: string) {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function Avatar({ src, alt, fallback, size = 'md', className }: AvatarProps) {
  const [hasError, setHasError] = React.useState(false);

  const initials = getInitials(fallback || alt);

  if (src && !hasError) {
    return (
      <img
        src={src}
        alt={alt || 'Avatar'}
        onError={() => setHasError(true)}
        className={cn(
          'rounded-full object-cover',
          sizeClasses[size],
          className
        )}
      />
    );
  }

  if (initials) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-gray-200 text-gray-600 font-medium',
          sizeClasses[size],
          className
        )}
        title={alt || fallback}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-gray-200 text-gray-400',
        sizeClasses[size],
        className
      )}
    >
      <User className="h-1/2 w-1/2" />
    </div>
  );
}

export { Avatar };
